import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import { Button, FormLabel } from "components/ui";
import { Colors } from "consts/colors";
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
} from "./Elements";
import { useAppDispatch } from "hooks/redux";
import { useStyledSnackbar } from "hooks/notification";
import { actions } from "store/patientDetail";

import { FileCard, FileUploadArea } from "components/common";
import { Grid } from "@mui/material";
import { Tfiles, TfileTypes } from "./types";
import { IFile } from "lib/model";

const isUploadDisabled = (files: Tfiles) => {
  for (const key in files) {
    if (files[key as TfileTypes]?.length) return false;
  }
  return true;
};

export const UploadFilesButton = () => {
  const dispatch = useAppDispatch();
  const { showErrorNotice } = useStyledSnackbar();
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<Tfiles>({});
  const openHandler = () => {
    setOpen(true);
  };
  const closeHandler = () => {
    setOpen(false);
  };
  const filesDeleteHandler = (fileType: TfileTypes) => (file: IFile | File) => {
    const fileName = file.name ?? "";
    setFiles((prevFiles) => ({
      ...prevFiles,
      [fileType]:
        prevFiles[fileType]?.filter((file) => file.name !== fileName) || [],
    }));
  };

  const filesUploadHandler = (fileType: TfileTypes) => (params: File[]) => {
    setFiles((prev) => ({
      ...prev,
      [fileType]: [...(prev[fileType] || []), ...params],
    }));
  };
  const uploadHandler = () => {
    setLoading(true);
    dispatch(actions.uploadCaseFiles(files))
      .unwrap()
      .then(() => {
        setFiles({});
        setOpen(false);
      })
      .catch((e) => showErrorNotice(e.message))
      .finally(() => setLoading(false));
  };
  return (
    <>
      <Dialog fullWidth maxWidth="md" open={open}>
        <StyledDialogTitle>
          <IconButton size="small" onClick={closeHandler}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </StyledDialogTitle>
        <Typography mx={5} variant="h2" color={Colors.PRIMARY}>
          File Upload
        </Typography>
        <StyledDialogContent>
          <Stack spacing={0} mb={8} mt={6}>
            <Grid container spacing={4} mt={0}>
              <FileUploadSection
                key={"ORIGIN_MODEL"}
                label="Upload your scans (STL format)"
                targetLabel="STL files"
                fileType="ORIGIN_MODEL"
                files={files?.ORIGIN_MODEL}
                onFilesUpload={filesUploadHandler("ORIGIN_MODEL")}
                onDelete={filesDeleteHandler("ORIGIN_MODEL")}
              />

              <FileUploadSection
                key={"CLINICAL_PHOTO"}
                label="Upload your Clinical Photos"
                targetLabel="Clinical photos"
                fileType="CLINICAL_PHOTO"
                files={files?.CLINICAL_PHOTO}
                onFilesUpload={filesUploadHandler("CLINICAL_PHOTO")}
                onDelete={filesDeleteHandler("CLINICAL_PHOTO")}
              />

              <FileUploadSection
                key={"OTHERS"}
                label="Others Files (ex: X-rays)"
                fileType="OTHERS"
                files={files?.OTHERS}
                onFilesUpload={filesUploadHandler("OTHERS")}
                onDelete={filesDeleteHandler("OTHERS")}
              />
            </Grid>
          </Stack>
        </StyledDialogContent>
        <StyledDialogActions>
          <Button
            disabled={isUploadDisabled(files)}
            variant="contained"
            fatButton
            loading={loading}
            onClick={uploadHandler}
          >
            Upload
          </Button>
        </StyledDialogActions>
      </Dialog>
      <Button
        data-intercom-target="Upload files button"
        onClick={openHandler}
        variant="contained"
        startIcon={<AddIcon />}
      >
        Add File
      </Button>
    </>
  );
};

const FileUploadSection = ({
  key,
  label,
  targetLabel,
  files,
  onFilesUpload,
  onDelete,
}: {
  key:string
  label: string;
  targetLabel?: string;
  fileType: TfileTypes;
  files?: File[];
  onFilesUpload: (files: File[]) => void;
  onUpload?: (params: File[]) => void;
  onDelete?: (file: IFile | File) => void;
}) => (
  <Grid item md={4} key={key}>
    <FormLabel label={label} targetLabel={targetLabel}>
      <FileUploadArea onFilesUpload={onFilesUpload} />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {files?.map((file,i) => (
          <FileCard
            key={i}
            file={file}
            onDelete={onDelete}
          />
        ))}
      </Stack>
    </FormLabel>
  </Grid>
);
